import React, { useEffect, useState } from 'react';
import { Button, Typography, IconButton, Grid, Modal, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { CommonTextField } from 'gemlux-common-components';
import { styled as materialStyled } from '@mui/material/styles';
import { subordinateActions } from '../../../../redux/subordinate/subordinateSlice';
import { theme } from '../../../../theme/theme';

const CustomButton = materialStyled(Button)(({ selected }: { selected: boolean }) => ({
  borderRadius: '8px',
  padding: '6px 16px',
  textTransform: 'none',
  fontFamily: 'Satoshi',
  fontSize: 14,
  fontWeight: 500,
  border: `1px solid ${selected ? theme.palette.shades.lightBlueColor : theme.palette.divider}`,
  backgroundColor: selected ? theme.palette.shades.lightBrightBlue : 'transparent',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: selected ? theme.palette.shades.lightBrightBlue : 'transparent',
  },
}));

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.default',
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[5],
  width: '400px',
  outline: 'none',
};

const CustomMainButton = materialStyled(Button, {
  shouldForwardProp: (prop) => prop !== 'plain',
})<{ plain: boolean }>(({ plain, disabled }) => ({
  backgroundColor: plain ? 'transparent' : '#3348FF',
  border: '1px solid #DCE0E5',
  width: '120px',
  borderRadius: '8px',
  padding: '8px 12px',
  color: plain ? '#14181F' : '#fff',
  boxShadow: plain
    ? ''
    : '0px 12px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px -2px 2px 0px rgba(48, 48, 48, 0.10) inset',
  textTransform: 'none',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  '&:hover': {
    backgroundColor: plain ? '#f5f5f7' : '#3030c9',
  },
}));

const SubordinateFilterModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [status, setStatus] = useState<string[]>([]);
  const [minUploads, setMinUploads] = useState<number | undefined>(undefined);
  const [maxUploads, setMaxUploads] = useState<number | undefined>(undefined);
  const [hasInteracted, setHasInteracted] = useState(false);
  const dispatch = useDispatch();

  const isFilterSelected = status.length > 0 || minUploads !== undefined || maxUploads !== undefined;

  const handleApply = () => {
    dispatch(
      subordinateActions.setFilters({
        status: status.length > 0 ? status.map((s) => s.toLowerCase()).join(',') : undefined,
        minUploads: minUploads || undefined,
        maxUploads: maxUploads || undefined,
      }),
    );
    dispatch(subordinateActions.fetchSubordinatesRequest());
    onClose();
  };

  const handleReset = () => {
    dispatch(
      subordinateActions.setFilters({
        status: undefined,
        minUploads: undefined,
        maxUploads: undefined,
      }),
    );
    setStatus([]);
    setMinUploads(undefined);
    setMaxUploads(undefined);
  };

  const statusOptions = ['Active', 'Inactive', 'Archived'];

  const onClickStatus = (value: string) => {
    if (status.includes(value)) {
      setStatus(status.filter((s) => s !== value));
      return;
    }
    setStatus([...status, value]);
  };

  useEffect(() => {
    if (isFilterSelected) {
      setHasInteracted(true);
    }
  }, [status, minUploads, maxUploads]);

  useEffect(() => {
    if (open && !isFilterSelected) {
      setHasInteracted(false);
    }
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Grid container sx={{ ...modalStyle }}>
        <Grid item xs={12} display="flex" justifyContent="space-between" p={2}>
          <Typography variant="title3Bold" textAlign="left" color="text">
            Filters
          </Typography>
          <IconButton size="small" aria-label="close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid container sx={{ p: 2 }} spacing={2}>
          <Grid item xs={12}>
            <Typography variant="footnoteRegular2" color="text" gutterBottom sx={{ paddingTop: '2%' }}>
              Status
            </Typography>
            <Grid container spacing={1}>
              {statusOptions.map((item) => (
                <Grid item key={item}>
                  <CustomButton onClick={() => onClickStatus(item)} selected={status.includes(item)}>
                    {item}
                  </CustomButton>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="footnoteRegular2" color="text" gutterBottom>
              No. of Gemstones
            </Typography>
            <Grid container spacing={2} sx={{ paddingTop: '2%' }}>
              <Grid item xs={6}>
                <Typography variant="textBase500" color="text">
                  Min
                </Typography>
                <CommonTextField
                  type="number"
                  placeholder="0"
                  value={minUploads ?? ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    setMinUploads(value === '' ? undefined : Number(value));
                  }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="textBase500" color="text">
                  Max
                </Typography>
                <CommonTextField
                  type="number"
                  placeholder="0"
                  value={maxUploads ?? ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    setMaxUploads(value === '' ? undefined : Number(value));
                  }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12} sm={12} display="flex" justifyContent="space-between" sx={{ padding: '3%' }}>
          <CustomMainButton plain disabled={!isFilterSelected} onClick={handleReset}>
            <Typography variant="textBase500">Reset Filters</Typography>
          </CustomMainButton>
          <CustomMainButton plain={false} disabled={!hasInteracted && !isFilterSelected} onClick={handleApply}>
            <Typography variant="textBase500">Apply Filters</Typography>
          </CustomMainButton>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default SubordinateFilterModal;
